/**
 * How loud the learner wants the tutor, on this device.
 *
 * Per device and not on the account, deliberately: the thing being corrected
 * for is a speaker and a room, not a person. The same learner on a phone in a
 * kitchen and on a laptop in a bedroom wants two different answers, and an
 * account-level setting would make each one overwrite the other.
 *
 * `playback.ts` calibrates the level that reaches this control -- +18 dB into
 * a compressor and back out -- against a measured sample. This is the human
 * trim on top of that calibration, not a replacement for it.
 */

/** Where the preference lives. Namespaced so it cannot collide. */
const KEY = 'tutor.volume';

/**
 * The slider's range, and where the calibrated level sits within it.
 *
 * `CALIBRATED` rather than the maximum, so the control goes both ways. Above
 * it there is real headroom to give: the calibrated chain peaks at -5.0 dBFS,
 * so the +3.9 dB that position 100 asks for lands at -1.1 dBFS and still does
 * not clip. Raising `MAX` past 100 would.
 */
export const MIN_VOLUME = 0;
export const MAX_VOLUME = 100;
export const CALIBRATED_VOLUME = 80;

/**
 * Slider position to a linear gain multiplier.
 *
 * Squared rather than linear. Loudness is perceived roughly logarithmically,
 * so a linear slider spends most of its travel in a range that all sounds
 * equally loud and then collapses to silence at the very bottom. Squaring
 * spreads the useful adjustment across the whole control: half travel is a
 * quarter of the power, about -12 dB, which is a large and *usable* step
 * rather than a barely audible one.
 *
 * Anchored so that `CALIBRATED_VOLUME` returns exactly 1 -- the measured level
 * `playback.ts` tuned, unmodified. A learner who never touches this control
 * hears what was calibrated, and that is the point of anchoring rather than
 * normalising to the maximum.
 */
export function volumeToGain(position: number): number {
  const clamped = Math.min(MAX_VOLUME, Math.max(MIN_VOLUME, position));
  return (clamped / CALIBRATED_VOLUME) ** 2;
}

/**
 * The stored preference, or the calibrated default.
 *
 * Every failure answers with the default rather than throwing. `localStorage`
 * throws outright in a private window and in a browser with site data blocked,
 * and returns whatever a previous version wrote in every other case -- so a
 * non-numeric, out-of-range or absent value all mean the same thing here:
 * nobody has usefully expressed a preference, so use the calibrated level.
 * Failing loudly would cost a conversation; failing safe costs one slider.
 */
export function readVolume(storage: Storage | undefined = globalThis.localStorage): number {
  try {
    const raw = storage?.getItem(KEY);
    if (raw === null || raw === undefined) return CALIBRATED_VOLUME;
    const parsed = Number(raw);
    if (!Number.isFinite(parsed)) return CALIBRATED_VOLUME;
    return Math.min(MAX_VOLUME, Math.max(MIN_VOLUME, parsed));
  } catch {
    return CALIBRATED_VOLUME;
  }
}

/**
 * Store a preference, clamped to the range the slider offers.
 *
 * Clamped on the way in as well as on the way out: a value written by a
 * previous version, or by hand, must not be able to ask for a gain that
 * clips.
 */
export function writeVolume(position: number, storage: Storage | undefined = globalThis.localStorage): void {
  if (!Number.isFinite(position)) return;
  const clamped = Math.min(MAX_VOLUME, Math.max(MIN_VOLUME, position));
  try {
    storage?.setItem(KEY, String(clamped));
  } catch {
    // Site data blocked, or the quota is full. The session still plays at the
    // level chosen; it just will not be remembered next time, which is not
    // worth interrupting anybody over.
  }
}
